import { useEffect, useCallback } from 'react'
import { useReducedMotion } from 'motion/react'

export interface KeyboardShortcutsConfig {
  onEnter?: () => void
  onEscape?: () => void
  onArrowLeft?: () => void
  onArrowRight?: () => void
  onUndo?: () => void
  onRedo?: () => void
  onRecalculate?: () => void
  enabled?: boolean
  excludeInputs?: boolean
}

export function useKeyboardShortcuts(config: KeyboardShortcutsConfig) {
  const {
    onEnter,
    onEscape,
    onArrowLeft,
    onArrowRight,
    onUndo,
    onRedo,
    onRecalculate,
    enabled = true,
    excludeInputs = true,
  } = config

  const prefersReducedMotion = useReducedMotion()

  const handleKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (!enabled) return

      if (excludeInputs) {
        const target = event.target as HTMLElement
        const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable
        if (isInput && event.key !== 'Escape') return
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const metaKey = isMac ? event.metaKey : event.ctrlKey

      switch (event.key) {
        case 'Enter':
          if (!metaKey && !event.shiftKey) {
            event.preventDefault()
            onEnter?.()
          }
          break
        case 'Escape':
          event.preventDefault()
          onEscape?.()
          break
        case 'ArrowLeft':
          if (!metaKey && !event.shiftKey) {
            event.preventDefault()
            onArrowLeft?.()
          }
          break
        case 'ArrowRight':
          if (!metaKey && !event.shiftKey) {
            event.preventDefault()
            onArrowRight?.()
          }
          break
        case 'z':
          if (metaKey && !event.shiftKey) {
            event.preventDefault()
            onUndo?.()
          } else if (metaKey && event.shiftKey) {
            event.preventDefault()
            onRedo?.()
          }
          break
        case 'y':
          if (metaKey && !event.shiftKey) {
            event.preventDefault()
            onRedo?.()
          }
          break
        case 'r':
          if (metaKey && event.shiftKey) {
            event.preventDefault()
            onRecalculate?.()
          }
          break
      }
    },
    [enabled, excludeInputs, onEnter, onEscape, onArrowLeft, onArrowRight, onUndo, onRedo, onRecalculate]
  )

  useEffect(() => {
    if (!enabled) return

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, handleKeyDown])

  return {
    prefersReducedMotion,
  }
}

export type { KeyboardShortcutsConfig as UseKeyboardShortcutsConfig }